"use client";

import { usePathname } from "next/navigation";
import { ExternalLink } from "lucide-react";
import CustomButton from "./CustomButton";
import CallExpandBtn from "./CallExpandBtn";
import { ThemeName, THEME_HEX } from "./theme";
import { ROUTES } from "@/lib/routes";
import { trackBookAppointmentClicked } from "@/lib/analytics";

export type BookingServiceType =
  | "counselling"
  | "life-coach"
  | "occupational-therapy"
  | "psychology";

const COUNSELLING_PATHS = [
  "/counselling",
  "/anger-management",
  "/relationships",
  "/depression-anxiety",
  "/grief-loss",
  "/career-counselling",
  "/life-coach",
  "/self-esteem-confidence",
  "/time-management",
  "/focus-productivity",
  "/changing-habits",
  "/public-speaking",
];

const ALLIED_HEALTH_PATHS = [
  "/allied-health",
  "/occupational-therapy",
  "/psychology",
];

const matchesPath = (paths: string[], pathname: string) =>
  paths.some((p) => pathname === p || pathname.startsWith(p + "/"));

const hours = [
  { day: "Monday", time: "9:00 AM – 8:00 PM" },
  { day: "Tuesday", time: "9:00 AM – 8:00 PM" },
  { day: "Wednesday", time: "9:00 AM – 8:00 PM" },
  { day: "Thursday", time: "9:00 AM – 8:00 PM" },
  { day: "Friday", time: "9:00 AM – 8:00 PM" },
  { day: "Saturday", time: "10:00 AM – 6:00 PM" },
  { day: "Sunday", time: "10:00 AM – 6:00 PM" },
];

export interface LocationsMapProps {
  theme?: Exclude<ThemeName, "white">;
  bookingService?: BookingServiceType;
  subtitle?: string;
  title?: string;
  description?: string;
  className?: string;
}

const LocationsMap = ({
  theme = "blue",
  bookingService,
  subtitle = "Our Practice Locations",
  title = "Visit Our Counselling & Allied Health Hubs in Sydney",
  description = "Conveniently situated in Parramatta and Blacktown, our modern Sydney consulting hubs offer confidential, welcoming environments for in-person therapy, coaching, and allied health assessments. We also provide flexible online telehealth appointments for clients across Greater Sydney and Australia-wide.",
  className = "",
}: LocationsMapProps) => {
  const themeColor = THEME_HEX[theme];
  const bookingUrl = bookingService
    ? `${ROUTES.booking}?service=${bookingService}`
    : ROUTES.booking;
  const pathname = usePathname();
  const isCounsellingPage = matchesPath(COUNSELLING_PATHS, pathname);
  const isAlliedHealthPage = matchesPath(ALLIED_HEALTH_PATHS, pathname);
  const showCounselling = !isAlliedHealthPage;
  const showAlliedHealth = !isCounsellingPage;
  const showLabels = showCounselling && showAlliedHealth;

  return (
    <section
      aria-label="Office location"
      className={`bg-[#F7FAFF] ${className}`}
    >
      <div className="mx-auto flex w-[90%] flex-col items-center justify-center gap-4 py-5 lg:w-[55%] lg:py-10 2xl:py-20">
        <h3 className="font-crimsonText text-[28px] font-bold">{subtitle}</h3>
        <h2
          className={`heading text-center ${theme === "blue" ? "text-v3-secondary" : "text-black"}`}
        >
          {title}
        </h2>
        <p className="text-center font-onest text-[18px] font-normal leading-[110%] text-black/70">
          {description}
        </p>
      </div>
      <div className="container-noGap relative w-full overflow-hidden lg:h-[750px]">
        <div>
          {/* Map — fixed height on mobile, fills section absolutely on desktop */}
          <div className="relative h-[260px] lg:absolute lg:inset-0 lg:h-auto">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3315.7!2d150.9061!3d-33.7688!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6b12a393f9f9f9f9%3A0x0!2sSuite%20207A%2C%2030%20Campbell%20St%2C%20Blacktown%20NSW%202148!5e0!3m2!1sen!2sau!4v1"
              width="100%"
              height="100%"
              style={{ border: 0, filter: "grayscale(20%)" }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Office location map"
              className="absolute inset-0 h-full w-full"
            />
            {/* Top White Fadeout */}
            <div className="pointer-events-none absolute left-0 right-0 top-0 z-10 h-[60px] bg-gradient-to-b from-white to-transparent lg:h-[120px]" />
            {/* Bottom White Fadeout */}
            <div className="pointer-events-none absolute bottom-0 left-0 right-0 z-10 h-[60px] bg-gradient-to-t from-white to-transparent lg:h-[120px]" />
          </div>

          {/* Container overlay — aligns card with the page layout */}
          <div className="container-x pointer-events-none lg:absolute lg:inset-0 lg:flex lg:items-center lg:justify-end">
            {/* Info card — below map on mobile, overlaid on desktop */}
            <div
              className="pointer-events-auto relative z-20 mx-4 my-5 flex flex-col gap-5 rounded-[20px] border bg-white p-5 lg:mx-0 lg:my-0 lg:w-[580px] lg:bg-white/80 lg:backdrop-blur-md"
              style={{ borderColor: themeColor }}
              aria-label="Office information"
            >
              {/* Location */}
              <div className="flex flex-col gap-3 px-2.5 py-2.5">
                <span
                  className="font-crimsonText text-[22px] font-bold lg:text-[24px]"
                  style={{ color: themeColor }}
                >
                  Location
                </span>
                <div className="flex flex-col gap-2">
                  {showCounselling && (
                    <div>
                      {showLabels && (
                        <span
                          className="font-onest text-[14px] font-semibold"
                          style={{ color: themeColor }}
                        >
                          Counselling &amp; Life Coach
                        </span>
                      )}
                      <address className="font-onest text-[16px] font-light not-italic leading-[140%] text-[#666]">
                        <a
                          href="https://maps.app.goo.gl/aJ1h9VTjYA5fxavb8"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group inline-flex items-start gap-2 hover:underline"
                        >
                          <span>
                            Suite 207a, 30 Campbell St
                            <br />
                            Blacktown, NSW 2148, Australia
                          </span>
                          <ExternalLink className="h-4 w-4 shrink-0 text-primary-300 transition-colors group-hover:text-primary-600" />
                        </a>
                      </address>
                    </div>
                  )}
                  {showAlliedHealth && (
                    <div>
                      {showLabels && (
                        <span
                          className="font-onest text-[14px] font-semibold"
                          style={{ color: themeColor }}
                        >
                          Allied Health
                        </span>
                      )}
                      <address className="font-onest text-[16px] font-light not-italic leading-[140%] text-[#666]">
                        <a
                          href="https://maps.app.goo.gl/9Z4jpyaDuSZKzyX6A"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group inline-flex items-start gap-2 hover:underline"
                        >
                          <span>
                            Suite 15.01, Level 15, 60 Station St East
                            <br />
                            Parramatta NSW 2150
                          </span>
                          <ExternalLink className="h-4 w-4 shrink-0 text-primary-300 transition-colors group-hover:text-primary-600" />
                        </a>
                      </address>
                    </div>
                  )}
                </div>
              </div>

              {/* Opening hours */}
              <div className="flex flex-col">
                <div
                  className="border-b px-2.5 py-2.5"
                  style={{ borderColor: themeColor }}
                >
                  <span className="font-onest text-[16px] font-bold text-[#333]">
                    Opening Hours
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-y-1 px-2.5 py-2.5">
                  {hours.map(({ day, time }) => (
                    <div key={day} className="contents">
                      <span className="font-onest text-[15px] font-normal text-[#333] lg:text-[16px]">
                        {day}:
                      </span>
                      <span className="font-onest text-[15px] font-light text-[#666] lg:text-[16px]">
                        {time}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTAs */}
              <div className="flex flex-col items-center gap-3 px-2.5 sm:flex-row sm:gap-4">
                <CallExpandBtn theme={theme} />
                <CustomButton
                  label="Book an Appointment"
                  href={bookingUrl}
                  variant="primary"
                  withIcon
                  size="lg"
                  theme={theme}
                  onClick={() =>
                    trackBookAppointmentClicked({
                      location: "locations_map",
                      service: bookingService,
                    })
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LocationsMap;
